import {
  EntityOperation,
  logEntityOperationFailure,
} from 'serviceWorker/helpers/entityFailureLog';
import { swSignals } from 'serviceWorker/events/swSignals';
import * as permissions from 'serviceWorker/db/permissions';
import { debounceNext } from 'serviceWorker/helpers/debounce';
import { createAbortOnLogout } from 'serviceWorker/helpers/abortableServiceHandler';
import { broadcastCurrentUserPermissions } from 'serviceWorker/broadcasts/currentUser';
import { identifyCurrentUser } from 'serviceWorker/services/currentUser/identity';
import { LogLevel } from 'shared/types/logger';
import { UserPermissionDto } from 'shared/types/userRole';
import { ProjectPermissionDto } from 'shared/domain/user/types/inDto';
import { debugLog } from 'shared/logger/debugLog';
import { ensureAuthenticatedConfig } from '../config';
import { get as getSelectedProject } from 'serviceWorker/db/selectedProject';
import { fetchPermissions } from './fetchPermissions';
import { arePermissionsEqual, toUserPermissions } from './model';
import {
  hasOrgAdminAccessChanged,
  hasBecameSuperAdmin,
  hasLostSuperAdmin,
} from 'shared/domain/permissions/basePermissionsGuards';
import { hasAccessibleProjectsChanged } from 'shared/domain/permissions/projectPermissionsGuards';
import { projectsSync } from '../projects';
import { rateLimitManager } from 'serviceWorker/api/RateLimitManager';
import { UserPermissions } from './types';
import { requestApi } from 'serviceWorker/api/requestApi';
import type { UserWithPermissionsInDto } from 'shared/domain/user/types/inDto';

const PERMISSIONS_MIN_INTERVAL_MS = 30_000;
const PERMISSIONS_PULL_DEBOUNCE_MS = 3_000;

type PermissionsResponse = Awaited<ReturnType<typeof fetchPermissions>>;

let abortManager: ReturnType<typeof createAbortOnLogout> | null = null;
let previousBasePermissions: UserPermissionDto | undefined;
let previousProjectPermissions: ProjectPermissionDto[] | undefined;
let inflightPermissionsPull: Promise<void> | null = null;
let inflightCurrentUserRefresh: Promise<UserWithPermissionsInDto> | null = null;
let lastAttemptAt = 0;
let lastSuccessfulAt = 0;

function resetPermissionsStateOnLogout(): void {
  previousBasePermissions = undefined;
  previousProjectPermissions = undefined;
  lastAttemptAt = 0;
  lastSuccessfulAt = 0;
}

/** Data of the old permissions is cleared (the data clearer and the running requests listen to it). */
function notifyPermissionsChanged(): void {
  swSignals.permissionsChanged.emit();
}

function shouldInvalidateProjectsCache(
  basePermissions: UserPermissionDto,
  projectPermissions: ProjectPermissionDto[]
): boolean {
  return (
    hasOrgAdminAccessChanged(previousBasePermissions, basePermissions) ||
    hasBecameSuperAdmin(previousBasePermissions, basePermissions) ||
    hasLostSuperAdmin(previousBasePermissions, basePermissions) ||
    hasAccessibleProjectsChanged(
      previousProjectPermissions,
      projectPermissions
    )
  );
}

async function applyPermissionsUpdate(
  resData: PermissionsResponse,
  broadcastSource: string
): Promise<UserPermissions[]> {
  identifyCurrentUser({ id: resData.userData._id, email: resData.userData.email });
  broadcastCurrentUserPermissions(resData, broadcastSource);
  const currentProjectsPermissions = toUserPermissions(resData);

  const shouldInvalidateProjects = shouldInvalidateProjectsCache(
    resData.userData.basePermissions,
    resData.userData.permissions
  );
  previousBasePermissions = resData.userData.basePermissions;
  previousProjectPermissions = resData.userData.permissions;

  await permissions.addBatch(currentProjectsPermissions);

  if (shouldInvalidateProjects) {
    // Downloaded again: the user gained or lost projects.
    projectsSync.sync();
  }

  return currentProjectsPermissions;
}

/**
 * Applies the downloaded user's permissions (stored, broadcast, the project
 * list synced when it changed) and tells the worker when the permissions to
 * the selected project changed, so the data of the old ones is cleared.
 */
async function applyDownloadedPermissions(
  download: () => Promise<PermissionsResponse>,
  broadcastSource: string
): Promise<UserWithPermissionsInDto> {
  const selectedProject = await getSelectedProject();
  const [previousPermissionToCurrentProject, resData] = await Promise.all([
    selectedProject ? permissions.getOne(selectedProject._id) : Promise.resolve(undefined),
    download(),
  ]);

  const currentProjectsPermissions = await applyPermissionsUpdate(resData, broadcastSource);
  lastSuccessfulAt = Date.now();

  if (previousPermissionToCurrentProject) {
    const permissionToCurrentProject = currentProjectsPermissions.find(
      (currentPermission) => currentPermission._id === previousPermissionToCurrentProject._id
    );
    if (!arePermissionsEqual(previousPermissionToCurrentProject, permissionToCurrentProject)) {
      debugLog('Permissions changed', previousPermissionToCurrentProject, permissionToCurrentProject);
      notifyPermissionsChanged();
    }
  }
  // Last: what waited for the permissions starts over the data they cleared, not before it.
  swSignals.permissionsStored.emit();
  return resData.userData;
}

async function syncPermissionsOnce(): Promise<void> {
  if (!abortManager) {
    return;
  }

  const abortController = abortManager.createController();
  const setup = await ensureAuthenticatedConfig();
  if (!setup) {
    swSignals.permissionsUnavailable.emit();
    return;
  }

  lastAttemptAt = Date.now();
  await rateLimitManager.waitUntilClear();

  try {
    await applyDownloadedPermissions(() => fetchPermissions(setup, abortController), 'runPermissionsPull');
  } catch (e: unknown) {
    logEntityOperationFailure(
      EntityOperation.FETCHING,
      { entityName: 'permissions', projectId: setup.projectId, error: e },
      LogLevel.INFO
    );
    swSignals.permissionsUnavailable.emit();
  }
}

/**
 * Downloads the logged-in user now, without the pulls' cooldown, applies its
 * permissions like a pull and resolves with the user; rejects when the
 * server refused (the page shows it). Refreshes asked while one runs get its
 * result.
 */
export function refreshCurrentUser(): Promise<UserWithPermissionsInDto> {
  inflightCurrentUserRefresh ??= downloadCurrentUser().finally(() => {
    inflightCurrentUserRefresh = null;
  });
  return inflightCurrentUserRefresh;
}

function downloadCurrentUser(): Promise<UserWithPermissionsInDto> {
  lastAttemptAt = Date.now();
  return applyDownloadedPermissions(
    async () => ({ userData: await requestApi<UserWithPermissionsInDto>({ method: 'GET', path: 'user/me' }) }),
    'refreshCurrentUser'
  );
}

/** One pull at a time: whoever asks while one runs gets its result. */
function pullPermissionsOnce(): Promise<void> {
  inflightPermissionsPull ??= syncPermissionsOnce().finally(() => {
    inflightPermissionsPull = null;
  });
  return inflightPermissionsPull;
}

async function runPermissionsPull(): Promise<void> {
  if (inflightPermissionsPull) {
    debugLog('permissions-pull-skipped-dedupe', { reason: 'inflight' });
    return inflightPermissionsPull;
  }

  const elapsedSinceAttempt = Date.now() - lastAttemptAt;
  if (lastAttemptAt > 0 && elapsedSinceAttempt < PERMISSIONS_MIN_INTERVAL_MS) {
    debugLog('permissions-pull-skipped-cooldown', {
      elapsedSinceAttemptMs: elapsedSinceAttempt,
      elapsedSinceSuccessMs:
        lastSuccessfulAt > 0 ? Date.now() - lastSuccessfulAt : null,
      minIntervalMs: PERMISSIONS_MIN_INTERVAL_MS,
    });
    return;
  }

  return pullPermissionsOnce();
}

/** test-only */
export function resetPermissionsPullStateForTests(): void {
  inflightPermissionsPull = null;
  inflightCurrentUserRefresh = null;
  lastAttemptAt = 0;
  lastSuccessfulAt = 0;
  previousBasePermissions = undefined;
  previousProjectPermissions = undefined;
}

export const init = (): void => {
  abortManager = createAbortOnLogout();
  swSignals.loggedOut.subscribe(resetPermissionsStateOnLogout);
  swSignals.entitySyncRequested.subscribe(
    debounceNext(runPermissionsPull, PERMISSIONS_PULL_DEBOUNCE_MS)
  );
  // Something waits for them: the cooldown protects the API from background syncs, not from this.
  swSignals.permissionsNeeded.subscribe(pullPermissionsOnce);
};
